<template>
    <app-layout title="Edit Customer">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Edit Customer</template>

                <template #actions>
                    <template v-if="!isActivated">
                        <jet-button
                            v-show="form.isDirty"
                            type="submit"
                            :disabled="form.processing"
                            >
                            Save
                        </jet-button>

                        <jet-tertiary-button
                            v-if="form.isDirty"
                            :disabled="form.processing"
                            @click="showUnsavedChangesModal = true"
                            >
                            Cancel
                        </jet-tertiary-button>

                        <jet-tertiary-button
                            v-else
                            :disabled="form.processing"
                            :href="setupUrl"
                            >
                            Close
                        </jet-tertiary-button>
                    </template>
                    <template v-else>
                        <jet-tertiary-button
                        v-if="favouriteProductCount > 0"
                        type="button"
                        class="py-1 min-h-8 inline-flex items-center gap-2"
                        :disabled="form.processing"
                        title="Export customer favourite products"
                        @click="openFavouritesExport"
                    >
                        <img src="/images/icons/excel.svg" width="14" height="14" class="shrink-0" alt="" />
                        <span class="whitespace-nowrap">Favourite Products</span>
                    </jet-tertiary-button>

                    <!-- Business Central Sync button -->
                        <jet-button @click="customerSync" :disabled="form.processing">
                            BC Sync
                        </jet-button>

                        <jet-button v-show="form.isDirty" type="submit" :disabled="form.processing">
                            Save
                        </jet-button>

                        <jet-tertiary-button
                            v-if="form.isDirty"
                            :disabled="form.processing"
                            @click="showUnsavedChangesModal = true"
                            >
                            Cancel
                        </jet-tertiary-button>

                        <jet-tertiary-button
                            v-else
                            :disabled="form.processing"
                            :href="backUrl"
                            >
                            Close
                        </jet-tertiary-button>
                    </template>
                </template>
            </page-header>

            <div v-if="isAdmin && customer.created_at" class="px-3 md:px-4 lg:px-8 mb-2 text-sm text-gray-500">
                Account Created on: {{ accountCreatedLabel }}
            </div>

            <customer-navigation :customer="customer" />

            <!-- Activation: list missing fields for un-activated customers -->
            <div
                v-if="!isActivated"
                class="px-8 pt-4 pb-4"
            >
                <div
                    :class="[
                        'p-4 border rounded',
                        activationMissingFieldsList.length > 0
                            ? 'bg-amber-50 border-amber-200'
                            : 'bg-green-50 border-green-200'
                    ]"
                >
                    <div class="flex items-start justify-between gap-4">
                        <div>
                            <div
                                v-if="activationMissingFieldsList.length > 0"
                                class="font-semibold text-amber-900"
                            >
                                {{ activationHeaderText }}
                            </div>
                            <div
                                v-else
                                class="font-semibold text-green-900"
                            >
                                All required fields are complete. Return to Setup to activate.
                            </div>
                            <ul
                                v-if="activationMissingFieldsList.length > 0"
                                class="mt-2 text-sm text-amber-800 list-disc list-inside"
                            >
                                <li v-for="label in activationMissingFieldsList" :key="label">{{ label }}</li>
                            </ul>
                            <div v-else class="mt-1 text-sm text-green-800">
                                <span v-if="customer.sales_rep">Only the customer's assigned sales representative ({{ customer.sales_rep.name }}) will be able to activate the customer.</span>
                                <span v-else>The customer can now be activated from the Setup page.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="px-8 xl:flex">
                <customer-form
                    :form="form"
                    :sales-reps="salesReps"
                    :type-options="typeOptions"
                    :route-options="routeOptions"
                    :pricing-options="pricingOptions"
                    :payment-terms-options="paymentTermsOptions"
                    :payment-method-options="paymentMethodOptions"
                    :show-enabled-toggle="isActivated"
                />

                <div class="w-full xl:w-2/5 xl:pl-4">
                    <delivery-schedule-form
                        ref="deliverySchedule"
                        :form="form"
                        :delivery-options="deliveryOptions"
                    />

                    <customer-pricing-form
                        v-if="hasFinanceRole"
                        class="mt-8"
                        :form="form"
                        :pricing-products-url="route('customers.pricing_products')"
                    />
                </div>
            </div>

            <div class="w-full text-center my-8">
                <jet-danger-button @click="showDeleteModal = true">Delete Customer</jet-danger-button>
            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :ignoreRoutes="[route('customers.onboarding.activate', customer.id)]"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="confirmUnsavedClose"
        />

        <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal=false">
            <template #title>
                Delete Customer
            </template>

            <template #content>
                Are you sure you would like to delete this customer?
            </template>

            <template #footer>
                <jet-tertiary-button @click="showDeleteModal=false">
                    Cancel
                </jet-tertiary-button>

                <jet-danger-button class="ml-2" @click="deleteCustomer" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Delete
                </jet-danger-button>
            </template>
        </jet-confirmation-modal>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import { usePage } from '@inertiajs/inertia-vue3';
import { DateTime } from 'luxon';
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import CustomerNavigation from '@/Shared/CustomerNavigation';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetNavLink from '@/Jetstream/NavLink';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import CustomerForm from '@/Pages/Customer/Partials/CustomerForm';
import CustomerPricingForm from '@/Pages/Customer/Partials/CustomerPricingForm';
import DeliveryScheduleForm from '@/Pages/Customer/Partials/DeliveryScheduleForm';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const form = useForm({
            id: props.customer.id,
            enabled: props.customer.enabled,
            sales_rep_id: props.customer.sales_rep_id,
            pricing_level_id: props.customer.pricing_level_id,
            tax_exempt: props.customer.tax_exempt,
            show_printed_order_pricing: props.customer.show_printed_order_pricing,
            pallet_orders: props.customer.pallet_orders,
            requires_printed_order: props.customer.requires_printed_order,
            type: props.customer.type,
            name: props.customer.name,
            address_1: props.customer.address_1,
            address_2: props.customer.address_2,
            city: props.customer.city,
            postal_code: props.customer.postal_code,
            province: props.customer.province,
            phone: props.customer.phone,
            cell: props.customer.cell,
            fax: props.customer.fax,
            email: props.customer.email,
            ap_email: props.customer.ap_email,
            contact_name: props.customer.contact_name,
            default_shipping_method: props.customer.default_shipping_method,
            route: props.customer.route,
            delivery_schedules: (props.customer.delivery_schedules?.length
                ? props.customer.delivery_schedules
                : [{ delivery_days: [], delivery_window_start: null, delivery_window_end: null }]
            ).map((row) => ({
                delivery_days: typeof row.delivery_days === 'string'
                    ? row.delivery_days.split(',').map((d) => d.trim()).filter(Boolean)
                    : [...(row.delivery_days || [])],
                delivery_window_start: row.delivery_window_start
                    ? String(row.delivery_window_start).slice(0, 5)
                    : null,
                delivery_window_end: row.delivery_window_end
                    ? String(row.delivery_window_end).slice(0, 5)
                    : null,
            })),
            delivery_duration: props.customer.delivery_duration,
            delivery_fee_override: props.customer.delivery_fee_override,
            delivery_order_minimum_override: props.customer.delivery_order_minimum_override,
            delivery_fee_below_minimum_override: props.customer.delivery_fee_below_minimum_override,
            auth_code: null,
            auto_apply_credit: props.customer.auto_apply_credit,
            pallet_orders: props.customer.pallet_orders,
            credit_balance: props.customer.credit_balance,
            internal_notes: props.customer.internal_notes,
            order_notes: props.customer.order_notes,
            load_notes: props.customer.load_notes,
            skills: Array.isArray(props.customer.skills) ? props.customer.skills : [],
            vehicle_features: Array.isArray(props.customer.vehicle_features) ? props.customer.vehicle_features : [],
            priority: 'M',
            picking_notes: props.customer.picking_notes,
            picking_red_flag: props.customer.picking_red_flag,
            payment_terms: props.customer.payment_terms,
            payment_method: props.customer.payment_method,
            send_inactive_alerts: props.customer.send_inactive_alerts,
            inactive_alert_weeks: props.customer.inactive_alert_weeks,
            inactive_alert_last_sent_at: props.customer.inactive_alert_last_sent_at,
            po_num: props.customer.po_num,
            invoice_emails: Array.isArray(props.customer.invoice_emails) 
                ? props.customer.invoice_emails.join(',') 
                : props.customer.invoice_emails,
            pricing: props.customer.pricing.map(pricing => {
                return {
                    product_id: pricing.product_id,
                    price: pricing.price.toFixed(2),
                    base_price: pricing.product.price,
                    market_cost: pricing.product.market_cost,
                    cost: pricing.product.cost,
                    title: pricing.product.title,
                    size: pricing.product.size,
                    modifier_type: pricing.modifier_type,
                    modifier_amount: pricing.modifier_amount
                }
            }),
            favourite_product_ids: [...(props.favouriteProductIds || [])],
        })

        const { hasFinanceRole, isAdmin } = UseUserAuth(usePage().props.value.user);

        return { form, hasFinanceRole, isAdmin }
    },

    components: {
        AppLayout,
        PageHeader,
        CustomerNavigation,
        JetButton,
        JetConfirmationModal,
        JetDangerButton,
        JetNavLink,
        JetTertiaryButton,
        CustomerForm,
        CustomerPricingForm,
        DeliveryScheduleForm,
        UnsavedChangesModal2,
    },

    props: [
        'customer',
        'canActivate',
        'activationMissingFields',
        'favouriteProductCount',
        'salesReps',
        'pricingOptions',
        'typeOptions',
        'routeOptions',
        'deliveryOptions',
        'paymentTermsOptions',
        'paymentMethodOptions',
        'favouriteProductIds',
    ],

    data() {
        return {
            formRoute: this.route('customers.update', this.customer.id),
            showDeleteModal: false,
            showUnsavedChangesModal: false,
        }
    },

    computed: {
        backUrl () {
            return route('customers.index', {
                page: this.$page.props.page.customers,
            });
        },

        setupUrl () {
            return route('customers.setup', this.customer.id);
        },

        isActivated() {
            return this.customer.onboarding_status == 'activated';
        },

        activationMissingFieldsList() {
            return Array.isArray(this.activationMissingFields) ? this.activationMissingFields : [];
        },

        activationHeaderText() {
            if (this.customer?.onboarding?.status !== 'payment_details_approved') {
                return 'The following fields must be set prior to customer activation:';
            }
            return 'The following fields must be set prior to customer activation:';
        },

        accountCreatedLabel () {
            if (!this.customer?.created_at) return '';
            const dt = DateTime.fromISO(this.customer.created_at);
            const datePart = dt.toFormat('MMMM d, yyyy');
            const timePart = dt.toFormat('h:mm a');
            return `${datePart} at ${timePart}`;
        },
    },

    methods: {
        async customerSync() {
            this.form.processing = true;
            try {
                const response = await axios.post('/business-central/customerSync', {
                    customer_id: this.form.id,
                    type: 'wholesale'
                });

                console.log(response.data.message);
            } catch (error) {
                console.error(error);
                
                if (error.response && error.response.data && error.response.data.message) {
                    console.error(error.response.data.message);
                } else {
                    console.error('An unknown error occurred.');
                }

                alert('An error occurred while syncing the product. Please check the eror logs.');
            } finally {
                this.form.processing = false;
            }
        },

        submit () {
            const run = () => {
                this.form
                    .transform(data => ({ ...data, setup_mode: 'sales' }))
                    .submit('put', route('customers.update', this.customer.id), {
                        onError: (errors) => {
                            // Schedule errors show first; only prompt auth when the server asks for it.
                            if (errors?.auth_code) {
                                this.form.auth_code = null;
                                this.$refs.deliverySchedule?.promptAuthForRetry?.();
                            }
                        },
                    });
            };

            if (this.$refs.deliverySchedule?.ensureAuthCode) {
                this.$refs.deliverySchedule.ensureAuthCode(run);
            } else {
                run();
            }
        },

        deleteCustomer () {
            this.$inertia.delete(route('customers.destroy', this.customer.id), null, {
                onError: (errors) => {
                    console.log(errors);
                }
            });
        },

        confirmUnsavedClose () {
            this.showUnsavedChangesModal = false;
            this.$inertia.get(this.isActivated ? this.backUrl : this.setupUrl);
        },

        openFavouritesExport () {
            const url = route('customers.favourites.excel_export', this.customer.id);
            window.open(url, '_blank');
        },
    },
}
</script>

<style scoped>

</style>
